import { useCallback } from "react";

import { isNull } from "lodash";
import { useDealActions } from "store";
import { CurrencyType, IImportedCommitment, MeasureParameter } from "store/types";

import {
  Errors,
  getDefaultGroup,
  getDefaultMeasureData,
  Group,
  IGroupsFormContext,
  MeasureData,
  MeasureDataErrors,
  MeasureDataRound
} from "../GroupsFormContext";

interface AcceptParams {
  setGroups: React.Dispatch<React.SetStateAction<Group[]>>;
  getMaxMeasureDataKey: () => number;
  setLoadingGroupId: React.Dispatch<React.SetStateAction<number | null>>;
  getDealRoundsFromState: (data: { dealId: number; filterDeletedRounds?: boolean }) => MeasureDataRound[] | null;
  setRounds: React.Dispatch<React.SetStateAction<Record<number, MeasureDataRound[]>>>;
  setMeasureData: React.Dispatch<React.SetStateAction<MeasureData[]>>;
  setMeasureDataErrors: React.Dispatch<React.SetStateAction<Record<number, MeasureDataErrors> | undefined>>;

  groups: Group[];
  deal_id: number;
  measureParameters: MeasureParameter[];
  defaultCurrency: CurrencyType;
  batch_id?: number;
  measureData: MeasureData[];
}

type UseGroup = (
  params: AcceptParams
) => Pick<IGroupsFormContext, "createGroup" | "updateGroup" | "deleteGroup" | "importCommitments">;

export const useGroup: UseGroup = ({
  setGroups,
  getMaxMeasureDataKey,
  setLoadingGroupId,
  getDealRoundsFromState,
  setRounds,
  setMeasureData,
  setMeasureDataErrors,

  groups,
  deal_id,
  measureParameters,
  defaultCurrency,
  batch_id,
  measureData
}) => {
  const { getDealRoundsWithInvestorsAction } = useDealActions();

  const buildMeasureData = useCallback(
    ({ rounds, group_id }: { rounds: MeasureDataRound[]; group_id: number }): MeasureData[] => {
      const maxKey = getMaxMeasureDataKey();
      let investorsCount = maxKey + 1;
      const newMeasureData: MeasureData[] = rounds.reduce((acc: MeasureData[], round) => {
        return [
          ...acc,
          ...round.investors.map(({ id, name, enable_split, investor_group }) => {
            const defaultMeasureData = getDefaultMeasureData({
              key: investorsCount,
              group_id,
              enable_split,
              investor_id: id,
              investor_name: name,
              round_id: round.id,
              investor_group: investor_group,
              // round_name: round.round_name,
              currency: defaultCurrency,
              ...(batch_id && { batch_id })
            });
            investorsCount++;
            return defaultMeasureData;
          })
        ];
      }, []);
      return newMeasureData;
    },
    [getMaxMeasureDataKey, defaultCurrency, batch_id]
  );

  const createGroup = useCallback<IGroupsFormContext["createGroup"]>(() => {
    const maxId = groups.reduce((acc, item) => (item.id > acc ? item.id : acc), 1);
    const newGroup = getDefaultGroup({ id: maxId + 1, ...{ deal_id } });
    setGroups((prevState) => [...prevState, newGroup]);
  }, [groups, setGroups]);

  const getLastMeasureDataDates = useCallback(
    (measureData: MeasureData[]): Pick<MeasureData, "date" | "accounting_date"> | null => {
      const lastMeasureData = measureData.sort((a, b) => a.key - b.key)[measureData.length - 1];
      if (!lastMeasureData) return null;
      return {
        date: lastMeasureData.date,
        accounting_date: lastMeasureData.accounting_date
      };
    },
    []
  );

  const updateMeasure = useCallback(
    async ({ group, measure_id }: { group: Group; measure_id: number | null }) => {
      const newMeasure = measure_id && measureParameters.find((item) => item.id === measure_id);
      const oldMeasure = group.measure_id && measureParameters.find((item) => item.id === group.measure_id);
      // Compare is it change of is_global
      const isEqualIsGlobal = Boolean(
        newMeasure && oldMeasure && newMeasure.is_global_value === oldMeasure.is_global_value
      );

      const originalMeasure =
        group.original_measure_id && measureParameters.find((item) => item.id === group.original_measure_id);

      // if measure_id is changed, we need to handle the old measure data
      // global > global: [], because we use existing measure data
      // global > not global: original_group_measure_data_ids, because we need to delete the old measure data
      // not global > global: original_group_measure_data_ids
      // not global > not global: original_group_measure_data_ids
      let delete_measure_data_ids: number[] = [];
      if (newMeasure && originalMeasure && newMeasure.id !== originalMeasure.id) {
        if (newMeasure.is_global_value && originalMeasure.is_global_value) {
          delete_measure_data_ids = []; // global > global
        } else {
          delete_measure_data_ids = group.original_group_measure_data_ids;
        }
      }

      let measureDataToSet: MeasureData[] = [...measureData];
      const updatedGroup: Group = {
        ...group,
        measure_id,
        delete_measure_data_ids
      };

      // Delete measure data if the measure was unselected
      if (isNull(measure_id)) {
        measureDataToSet = measureDataToSet.filter((item) => item.group_id !== group.id);
      }

      let lastMeasureDataDates = getLastMeasureDataDates(measureDataToSet);

      // is changing from global to not global or vice versa
      if (newMeasure && !isEqualIsGlobal) {
        // Delete previous measure data
        measureDataToSet = measureDataToSet.filter((item) => item.group_id !== group.id);

        // Use dates from last measure data
        lastMeasureDataDates = getLastMeasureDataDates(measureDataToSet);

        // Create only 1 item if it is global
        if (newMeasure.is_global_value) {
          measureDataToSet.push(
            getDefaultMeasureData({
              key: getMaxMeasureDataKey() + 1,
              group_id: group.id,
              investor_id: null,
              investor_name: null,
              round_id: null,
              // round_name: null,
              currency: defaultCurrency,
              value: String(group.total_amount || ""),
              ...(batch_id && { batch_id }),
              ...(originalMeasure &&
                originalMeasure?.is_global_value &&
                group.original_group_measure_data_ids?.length && {
                  id: group.original_group_measure_data_ids[0]
                }),
              ...lastMeasureDataDates
            })
          );
        } else {
          // Create many items if it is not global
          // Pick sub deal if group is oriented to sub deal
          const relevantDealId = updatedGroup.sub_deal_id || updatedGroup.deal_id;
          // Try to get rounds from state
          let rounds: MeasureDataRound[] | null = getDealRoundsFromState({
            dealId: relevantDealId
          });
          if (!rounds) {
            // Get rounds from API and put them to state
            rounds = (await getDealRoundsWithInvestorsAction({ dealId: relevantDealId, with_parent_round: true })).map(
              (item) => ({
                id: item.round_id,
                name: item.round_name,
                investors: item.investors,
                is_deleted: false
              })
            );
            setRounds((prevState) => ({ ...prevState, [relevantDealId]: rounds! }));
          }

          buildMeasureData({ rounds, group_id: group.id }).forEach((item) =>
            measureDataToSet.push({ ...item, ...lastMeasureDataDates })
          );
        }
      }
      setGroups((prevState) => prevState.map((item) => (item.id === updatedGroup.id ? updatedGroup : item)));
      setMeasureData(measureDataToSet);

      // Clear errors for measure data in the group
      const groupMeasureDataKeys = measureData.filter((item) => item.group_id === group.id).map((item) => item.key);
      if (groupMeasureDataKeys.length) {
        setMeasureDataErrors((prevState) => {
          if (!prevState) return prevState;
          const errorsCopy = { ...prevState };
          groupMeasureDataKeys.forEach((key) => delete errorsCopy[key]);
          return Object.keys(errorsCopy).length ? errorsCopy : undefined;
        });
      }
    },
    [
      setGroups,
      buildMeasureData,
      setMeasureData,
      getMaxMeasureDataKey,
      defaultCurrency,
      getDealRoundsFromState,
      setRounds,
      measureParameters,
      measureData,
      batch_id
    ]
  );

  const updateSubDeal = useCallback(
    async ({ group, sub_deal_id }: { group: Group; sub_deal_id: number | null }) => {
      const measure = measureParameters.find((item) => item.id === group.measure_id);
      const updatedGroup: Group = { ...group, sub_deal_id };

      if (measure && !measure.is_global_value) {
        const clearedMeasureData = measureData.filter((item) => item.group_id !== group.id);

        const lastMeasureDataDates = getLastMeasureDataDates(clearedMeasureData);

        // Pick sub deal if group is oriented to sub deal
        const relevantDealId = updatedGroup.sub_deal_id || updatedGroup.deal_id;
        // Try to get rounds from state
        let rounds: MeasureDataRound[] | null = getDealRoundsFromState({
          dealId: relevantDealId
        });
        if (!rounds) {
          // Get rounds from API and put them to state
          rounds = (await getDealRoundsWithInvestorsAction({ dealId: relevantDealId, with_parent_round: true })).map(
            (item) => ({
              id: item.round_id,
              name: item.round_name,
              investors: item.investors,
              is_deleted: false
            })
          );
          setRounds((prevState) => ({ ...prevState, [relevantDealId]: rounds! }));
        }

        buildMeasureData({ rounds, group_id: group.id }).forEach((item) =>
          clearedMeasureData.push({ ...item, ...lastMeasureDataDates })
        );
        setMeasureData(clearedMeasureData);
      }

      setGroups((prevState) => prevState.map((item) => (item.id === updatedGroup.id ? updatedGroup : item)));
    },
    [setGroups, buildMeasureData, setMeasureData, getDealRoundsFromState, setRounds, measureParameters]
  );

  const updateGroup = useCallback<IGroupsFormContext["updateGroup"]>(
    async ({ id, ...rest }) => {
      try {
        const group = groups.find((item) => item.id === id);

        if (!group) throw new Error(`${Errors.NO_GROUP_FOUND} ${id}`);

        setLoadingGroupId(group.id);

        if (rest.measure_id || isNull(rest.measure_id)) {
          updateMeasure({ group, measure_id: rest.measure_id });
        }

        if (rest.sub_deal_id || isNull(rest.sub_deal_id)) {
          updateSubDeal({ group, sub_deal_id: rest.sub_deal_id });
        }

        // setMeasureData((prevState) => {
        //   // Clear old data before refetch
        //   const clearedPrevState = prevState.filter((item) => item.group_id !== group.id);
        //   const lastMeasureData: MeasureData | false =
        //     Boolean(clearedPrevState.length) &&
        //     clearedPrevState.sort((a, b) => a.key - b.key)[clearedPrevState?.length - 1];

        //   if (lastMeasureData) {
        //     newMeasureData = newMeasureData.map((item) => {
        //       return {
        //         ...item,
        //         date: lastMeasureData.date || item.date,
        //         accounting_date: lastMeasureData.accounting_date || item.accounting_date
        //       };
        //     });
        //   }

        //   return [...clearedPrevState, ...newMeasureData];
        // });
      } catch (error) {
        console.log((error as Error).message);
      } finally {
        setLoadingGroupId(null);
      }
    },
    [groups, updateMeasure, updateSubDeal]
  );

  const importCommitments = useCallback<IGroupsFormContext["importCommitments"]>(
    async (data: IImportedCommitment[]) => {
      if (!data.length) return;
      try {
        const commitmentMeasureId = data[0].measure_id;

        // Remove any empty groups (groups without measure_id)
        const filteredGroups = groups.filter((group) => group.measure_id);

        // Calculate starting group ID
        let nextGroupId = filteredGroups.length === 0 ? 1 : Math.max(...filteredGroups.map((g) => g.id)) + 1;

        // The file may contain data for different dates, so we need to split the data by dates,
        // so that in the future we can create a group for each date
        const dataByDate = data.reduce(
          (acc, item) => {
            const date = item.date || "";
            if (!acc[date]) {
              acc[date] = [];
            }
            acc[date].push(item);
            return acc;
          },
          {} as Record<string, (typeof data)[0][]>
        );

        let rounds: MeasureDataRound[] | null = getDealRoundsFromState({
          dealId: deal_id
        });

        if (!rounds) {
          rounds = (await getDealRoundsWithInvestorsAction({ dealId: deal_id, with_parent_round: true })).map(
            (item) => ({
              id: item.round_id,
              name: item.round_name,
              investors: item.investors,
              is_deleted: false
            })
          );
          setRounds((prevState) => ({ ...prevState, [deal_id]: rounds! }));
        }

        const newGroups: Group[] = [];
        let allMeasureData: MeasureData[] = [];
        let currentKey = getMaxMeasureDataKey() + 1;

        // Each date group
        for (const [date, dateData] of Object.entries(dataByDate)) {
          const groupId = nextGroupId++;

          const newGroup = getDefaultGroup({
            id: groupId,
            deal_id,
            measure_id: commitmentMeasureId
          });
          newGroups.push(newGroup);

          // Process imported data for this date
          const importedData = dateData.map((item) => ({
            ...getDefaultMeasureData({
              key: currentKey++,
              group_id: groupId,
              investor_id: item.investor_id,
              investor_name: item.investor_name,
              round_id: item.round_id,
              currency: defaultCurrency,
              enable_split: false,
              ...(batch_id && { batch_id }),
              date: item.date
            }),
            ...item,
            group_id: groupId,
            key: currentKey - 1
          }));

          // Add empty measure data for investors that exist in other dates but not include in file
          const emptyMeasureData = rounds.reduce((acc: MeasureData[], round: MeasureDataRound) => {
            const roundInvestors = round.investors
              .filter(
                (investor) => !dateData.some((item) => item.round_id === round.id && item.investor_id === investor.id)
              )
              .map((investor) => {
                return getDefaultMeasureData({
                  key: currentKey++,
                  group_id: groupId,
                  enable_split: investor.enable_split,
                  investor_id: investor.id,
                  investor_name: investor.name,
                  round_id: round.id,
                  currency: defaultCurrency,
                  ...(batch_id && { batch_id }),
                  date: date,
                  value: ""
                });
              });
            return [...acc, ...roundInvestors];
          }, []);

          // Append new data
          allMeasureData = [...allMeasureData, ...importedData, ...emptyMeasureData];
        }

        // Update state
        setGroups([...filteredGroups, ...newGroups]);
        setMeasureData((prevState) => [...prevState, ...allMeasureData]);
      } catch (error) {
        console.error("Error in importCommitments:", error);
        throw error;
      }
    },
    [
      groups,
      deal_id,
      setGroups,
      setMeasureData,
      getMaxMeasureDataKey,
      getDealRoundsFromState,
      getDealRoundsWithInvestorsAction,
      setRounds,
      defaultCurrency,
      batch_id,
      updateGroup
    ]
  );

  const removeMeasureDataErrors = useCallback(
    (key: number) => {
      setMeasureDataErrors((prevState) => {
        if (!prevState) return;
        if (!prevState[key]) return prevState;

        const errorsCopy = { ...prevState };

        delete errorsCopy[key];

        return errorsCopy;
      });
    },
    [setMeasureDataErrors]
  );

  const deleteGroup = useCallback<IGroupsFormContext["deleteGroup"]>(
    (id) => {
      setGroups((prevState) => prevState.filter((group) => group.id !== id));
      // Remove measure data related to the group

      setMeasureData((prevState) =>
        prevState.filter((item) => {
          if (item.group_id !== id) return true;

          removeMeasureDataErrors(item.key);
          return false;
        })
      );
    },
    [setGroups, setMeasureData, removeMeasureDataErrors]
  );

  return {
    createGroup,
    updateGroup,
    deleteGroup,
    importCommitments
  };
};
